import { Controller } from "@hotwired/stimulus";
import { trapFocus, releaseFocusTrap } from "../utils/FocusTrap";
import {
  hideStudentChoiceExcludedContentModal,
  showStudentChoiceExcludedContentModal,
  studentChoiceExcludedContentResponse,
} from "../utils/student_choice_excluded_content_modal";

// Connects to data-controller="add-items-to-course"
export default class extends Controller {
  static targets = [
    "row",
    "totalTime",
    "caseCount",
    "scriptCount",
    "documents",
    "submitModal",
    "modalOpenButton",
  ];
  static values = {
    excludedSccDocumentIds: Array,
  };

  initialize() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
    document.addEventListener("keydown", this.handleKeyDown);
    this.closeSubmitModal();
    this.closeExcludedContentModal();
    this.enableOrDisableModalButton();
    this.clearEmptySearchParam();
  }

  // If url search param is present in url but it is empty, then remove it along with
  // the question mark if it is the only param
  clearEmptySearchParam() {
    const url = new URL(window.location.href);
    if (
      url.searchParams.has("search") &&
      url.searchParams.get("search") === ""
    ) {
      url.searchParams.delete("search");
      if (url.search === "") {
        // If there are no other search params, remove the question mark
        window.history.replaceState({}, document.title, url.pathname);
      } else {
        // If there are other search params, just update the URL
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }

  // Disable all modalOpenButtons if no rows are selected
  enableOrDisableModalButton() {
    const selected = this.getSelectedAddItemCheckboxHashes();
    // Update selection button text based on selected checkboxes
    this.modalOpenButtonTargets.forEach(btn => {
      const span = btn.querySelector('.add-items__button-text');
      if (selected.length > 0) {  
        btn.disabled = false;
        if (span) {
          span.textContent = `Add ${selected.length} Selection${selected.length > 1 ? 's' : ''} to Course`;
        }
      } else {
        btn.disabled = true;
        if (span) {
          span.textContent = 'Add Selection to Course';
        }
      }
    });
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.closeSubmitModal();
      if (this.isExcludedContentModalOpen()) {
        this.cancelExcludedContentModal();
      } else {
        this.closeExcludedContentModal();
      }
    }
  }

  connect() {
    // Initialize summary display when turbo load is complete
    document.addEventListener("turbo:load", () => {
      this.restoreSelectedCheckboxes();
      this.updateSummary();
    });

    this.updateStickyOffsetsBound = this.updateStickyOffsets.bind(this);

    // Other setup logic if you have any
    this.updateStickyOffsets();

    // Update offsets when window is resized
    window.addEventListener("resize", this.updateStickyOffsetsBound);

    this._resizeHandler = this.enableOrDisableModalButton.bind(this);
    window.addEventListener('resize', this._resizeHandler);
  }

  disconnect() {
    window.removeEventListener("resize", this.updateStickyOffsetsBound);
    window.removeEventListener('resize', this._resizeHandler);
    releaseFocusTrap(document.getElementById("student-choice-excluded-content-modal"));

    // Only keep localStorage if still on new_batch_list page for a document set
    const batchListRegex = /\/document_sets\/(\d+)\/document_set_document_relations\/new_batch_list(\?.*)?$/;
    if (!batchListRegex.test(window.location.pathname + window.location.search)) {
      // Remove all scc_selected_checkboxes_<docSetId> and add_item_selected_checkboxes_<docSetId> keys from localStorage
      Object.keys(localStorage).forEach(key => {
        if (/^scc_selected_checkboxes_\d+$/.test(key)) {
          localStorage.removeItem(key);
        }
        if (/^add_item_selected_checkboxes_\d+$/.test(key)) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  // Restore selected checkboxes from localStorage
  restoreSelectedCheckboxes() {
    const selected = this.getSelectedAddItemCheckboxHashes();
    const checkboxes = document.querySelectorAll('input.include-checkbox[data-action*="add-items-to-course#toggleRow"]');
    // Set the checked state of each checkbox based on localStorage
    checkboxes.forEach(cb => {
      const docId = cb.dataset.documentId;
      cb.checked = selected.some(item => item.id === docId);
    });
  }

  toggleRow(event) {
    const selected = this.getSelectedAddItemCheckboxHashes();
    const checked = event.target.checked;
    const docId = event.target.dataset.documentId;
    const documentTitle = event.target.dataset.documentTitle;
    const documentTime = event.target.closest('[data-document-time]')?.dataset.documentTime || event.target.dataset.documentTime;
    const documentType = event.target.closest('[data-document-type]')?.dataset.documentType || event.target.dataset.documentType;
    const key = this.getAddItemLocalStorageKey();
    if (checked) {
      if (!selected.some(item => item.id === docId)) {
        selected.push({ id: docId, title: documentTitle, time: documentTime, type: documentType });
        localStorage.setItem(key, JSON.stringify(selected));
      }
    } else {
      const idx = selected.findIndex(item => item.id === docId);
      if (idx !== -1) {
        selected.splice(idx, 1);
        localStorage.setItem(key, JSON.stringify(selected));
      }
    }
    this.enableOrDisableModalButton();
    this.updateSummary();
  }

  updateSummary() {
    const documentsDataSet = this.documentsTarget.dataset;
    const initialMinutes =
      +documentsDataSet.totalHoursValue * 60 +
      +documentsDataSet.totalMinutesValue;
    let totalMinutes = initialMinutes;
    let caseCount = +documentsDataSet.caseCountValue || 0;
    let scriptCount = +documentsDataSet.scriptCountValue;

    const selected = this.getSelectedAddItemCheckboxHashes();

    // Sum time and count for all selected rows using stored data
    selected.forEach(item => {
      const timeInSeconds = parseInt(item.time, 10) || 0;
      const timeInMinutes = Math.floor(timeInSeconds / 60);
      const type = item.type;

      totalMinutes += timeInMinutes;
      if (type === "signature_case") caseCount++;
      else if (type === "illness_script") scriptCount++;
    });

    this.enableOrDisableModalButton();
    this.updateTimeDisplay(totalMinutes);
    this.updateCountDisplay(caseCount, scriptCount);
  }

  updateTimeDisplay(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const timeString = `${hours} hour${
      hours !== 1 ? "s" : ""
    } ${minutes} minute${minutes !== 1 ? "s" : ""}`;
    this.totalTimeTargets.forEach((el) => (el.textContent = timeString));
  }

  updateCountDisplay(caseCount, scriptCount) {
    this.caseCountTargets.forEach(
      (el) =>
        (el.textContent = `${caseCount} Case${caseCount !== 1 ? "s" : ""}`)
    );
    this.scriptCountTargets.forEach(
      (el) =>
        (el.textContent = `${scriptCount} Script${
          scriptCount !== 1 ? "s" : ""
        }`)
    );
  }

  submitChecked() {
    const isStudent = this.modalOpenButtonTargets[0]?.dataset.isStudent === 'true';
    const selectedHashes = isStudent
      ? this.getSelectedCheckboxHashes()
      : this.getSelectedAddItemCheckboxHashes();
    
    let selectedDocumentIds;
    if (selectedHashes.length > 0) {
      selectedDocumentIds = selectedHashes.map(item => item.id);
    } else {
      selectedDocumentIds = [];
      this.rowTargets.forEach((row) => {
        const checkbox = row.querySelector(".include-checkbox");
        if (checkbox && checkbox.checked) {
          const id = row.dataset.documentId;
          if (id) selectedDocumentIds.push(id);
        }
      });
    }

    const url = window.location.pathname.replace("new_", "create_");

    // Submit selected documents to API (Accept: JSON so Rails uses format.json, not format.html)
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ documentIds: selectedDocumentIds }),
    })
      .then(async (response) => {
        const text = await response.text();
        const data = text.trim() ? JSON.parse(text) : {};
        if (studentChoiceExcludedContentResponse(response, data)) {
          this.closeSubmitModal();
          this.openExcludedContentModal();
          return null;
        }
        if (!response.ok) throw new Error("API call failed");
        return data;
      })
      .then((data) => {
        if (data === null) return;
        localStorage.setItem(this.getLocalStorageKey(), []);
        this.onSubmitSuccess();
      })
      .catch((error) => {
        console.error("API error:", error);
        alert("Something went wrong. Please try again.");
      });
  }

  openExcludedContentModal() {
    const modal = document.getElementById("student-choice-excluded-content-modal");
    showStudentChoiceExcludedContentModal();
    if (!modal) return;
    trapFocus(modal, {
      initialFocusSelector: "#student-choice-excluded-content-modal-title",
    });
  }

  openExcludedContentModalFromKeyboard(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    this.openExcludedContentModal();
  }

  closeExcludedContentModal() {
    const modal = document.getElementById("student-choice-excluded-content-modal");
    releaseFocusTrap(modal);
    hideStudentChoiceExcludedContentModal();
  }

  cancelExcludedContentModal() {
    this.closeExcludedContentModal();
    const destination = this.coursePagePathFromAddItemsLink() || this.coursePagePathFromCurrentUrl();
    if (destination) Turbo.visit(destination);
  }

  coursePagePathFromAddItemsLink() {
    const addItemsLink = document.querySelector("a[href*='/document_set_document_relations/new_batch_list']");
    if (!addItemsLink) return null;
    const href = addItemsLink.getAttribute("href");
    if (!href) return null;
    return href.replace(/\/document_set_document_relations\/new_batch_list(\?.*)?$/, "");
  }

  coursePagePathFromCurrentUrl() {
    const match = window.location.pathname.match(/^(\/document_sets\/\d+)/);
    return match ? match[1] : null;
  }

  isExcludedContentModalOpen() {
    const modal = document.getElementById("student-choice-excluded-content-modal");
    return Boolean(modal && modal.classList.contains("open") && !modal.classList.contains("hidden"));
  }

  onSubmitSuccess() {
    const editUrl = window.location.href;
    const updateUrl = editUrl.replace(
      "/document_set_document_relations/new_batch_list",
      ""
    );

    Turbo.visit(updateUrl);
  }

  openSubmitModal() {
    // Determine if user is student from data attribute
    const isStudent = this.modalOpenButtonTargets[0]?.dataset.isStudent === 'true';
    this.submitModalTargets.forEach(modal => {
      modal.classList.remove("hidden");
      modal.classList.add("open");
      this.printSelectedItems(modal, isStudent);
    });
  }

  printSelectedItems(modal, isStudent = false) {
    // Use the correct hash based on isStudent
    const selectedHashes = isStudent
      ? this.getSelectedCheckboxHashes()
      : this.getSelectedAddItemCheckboxHashes();

    // Append the selected items to cases container in the submitModal in p tag
    const casesContainer = modal.querySelector(
      "[data-add-items-to-course-target='cases']"
    );
    if (casesContainer) {
      if (selectedHashes.length > 0) {
        casesContainer.innerHTML = selectedHashes
          .map((item) => `<p>${item.title}</p>`)
          .join("");
      } else {
        const selectedItems = this.rowTargets
          .filter((row) => row?.querySelector(".include-checkbox")?.checked)
          .map((row) => row?.dataset?.documentTitle);
        casesContainer.innerHTML = selectedItems
          .map((item) => `<p>${item}</p>`)
          .join("");
      }
    }
  }

  closeSubmitModal() {
    this.submitModalTargets.forEach(modal => {
      modal.classList.remove("open");
      modal.classList.add("hidden");
    });
  }

  updateStickyOffsets() {
    const navigation = document.querySelector(".navigation");
    const firstSticky = document.querySelector(
      ".breadcrumb-header-panel-new-landing-pages"
    );
    const secondSticky = document.querySelector(
      ".add-items__title-search-container"
    );

    if (navigation && firstSticky) {
      const navHeight = navigation.offsetHeight;
      firstSticky.style.top = `${navHeight}px`;
    }

    if (navigation && firstSticky && secondSticky) {
      const firstHeight = navigation.offsetHeight + firstSticky.offsetHeight;
      secondSticky.style.top = `${firstHeight}px`;
    }
  }

  // Helper to get unique localStorage key based on document set ID in URL
  getLocalStorageKey() {
    const match = window.location.pathname.match(/\/document_sets\/(\d+)/);
    const docSetId = match ? match[1] : "default";
    return `scc_selected_checkboxes_${docSetId}`;
  }

  // Get selected checkbox from localStorage
  getSelectedCheckboxHashes() {
    const key = this.getLocalStorageKey();
    try {
      const selected = JSON.parse(localStorage.getItem(key)) || [];
      if (!Array.isArray(selected) || this.excludedSccDocumentIdsValue.length === 0) return selected;

      const excludedIds = new Set(this.excludedSccDocumentIdsValue.map(String));
      const filtered = selected.filter(item => !excludedIds.has(String(item?.id)));

      if (filtered.length !== selected.length) {
        localStorage.setItem(key, JSON.stringify(filtered));
      }

      return filtered;
    } catch {
      return [];
    }
  }

  // Helper to get unique add item localStorage key based on document set ID in URL
  getAddItemLocalStorageKey() {
    const match = window.location.pathname.match(/\/document_sets\/(\d+)/);
    const docSetId = match ? match[1] : "default";
    return `add_item_selected_checkboxes_${docSetId}`;
  }

  // Get selected add item checkbox from localStorage
  getSelectedAddItemCheckboxHashes() {
    const key = this.getAddItemLocalStorageKey();
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch {
      return [];
    }
  }
}
